import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../context/LanguageContext';
import './Training.css';

const courses = [
  {
    code: 'WAST',
    name: 'Web Application Security Testing',
    nameKey: 'agency.training_course_wast_title',
    level: 'Intermediate',
    icon: '/images/training/web-app-security.png',
    desc: 'Master the art of identifying and exploiting web vulnerabilities. Covers OWASP Top 10, SQL injection, XSS, CSRF, and advanced attack chains.',
    descKey: 'agency.training_course_wast_desc',
    topics: ['OWASP Top 10', 'Burp Suite', 'API Security', 'Authentication Attacks'],
    color: 'green',
  },
  {
    code: 'AWS-SEC',
    name: 'AWS Certified Security – Specialty',
    nameKey: 'agency.training_course_aws_sec_title',
    level: 'Advanced',
    icon: '/images/training/aws-security.png',
    desc: 'Validate your expertise in securing the AWS environment. Covers identity management, infrastructure protection, data protection, and incident response.',
    descKey: 'agency.training_course_aws_sec_desc',
    topics: ['IAM', 'KMS', 'CloudTrail', 'Security Hub'],
    color: 'orange',
  },
  {
    code: 'CND',
    name: 'Certified Network Defender',
    nameKey: 'agency.training_course_cnd_title',
    level: 'Intermediate',
    icon: '/images/training/network-defender.png',
    desc: 'Develop skills to protect, detect, and respond to network-based threats. Hands-on labs covering firewalls, IDS/IPS, VPNs, and network monitoring.',
    descKey: 'agency.training_course_cnd_desc',
    topics: ['Network Defense', 'IDS/IPS', 'Firewall Config', 'Incident Response'],
    color: 'teal',
  },
  {
    code: 'CCOA',
    name: 'Cisco Certified CyberOps Associate',
    nameKey: 'agency.training_course_ccoa_title',
    level: 'Beginner',
    icon: '/images/training/cisco-cyberops.png',
    desc: 'Gain the skills to work in SOC environments. Covers security monitoring, intrusion analysis, and incident response fundamentals.',
    descKey: 'agency.training_course_ccoa_desc',
    topics: ['SOC Operations', 'Security Monitoring', 'SIEM', 'Threat Analysis'],
    color: 'green',
  },
  {
    code: 'CCSP',
    name: 'Certified Cloud Security Professional',
    nameKey: 'agency.training_course_ccsp_title',
    level: 'Advanced',
    icon: '/images/training/cloud-security-pro.png',
    desc: 'Become a cloud security expert. Covers cloud architecture, data security, platform and infrastructure security, and legal/compliance requirements.',
    descKey: 'agency.training_course_ccsp_desc',
    topics: ['Cloud Architecture', 'Data Security', 'Compliance', 'Risk Management'],
    color: 'orange',
  },
  {
    code: 'CFR',
    name: 'CyberSec First Responder',
    nameKey: 'agency.training_course_cfr_title',
    level: 'Intermediate',
    icon: '/images/training/cybersec-first-responder.png',
    desc: 'Prepare to identify, contain, eradicate, and recover from cybersecurity incidents. Covers incident handling, threat intelligence, and forensics basics.',
    descKey: 'agency.training_course_cfr_desc',
    topics: ['Incident Handling', 'Threat Intel', 'Digital Forensics', 'Malware Analysis'],
    color: 'teal',
  },
  {
    code: 'CEH',
    name: 'Certified Ethical Hacker',
    nameKey: 'agency.training_course_ceh_title',
    level: 'Intermediate',
    icon: '/images/training/ethical-hacker.png',
    desc: 'Think like a hacker, defend like a pro. Master 20 hacking domains including foot-printing, scanning, enumeration, and session hijacking.',
    descKey: 'agency.training_course_ceh_desc',
    topics: ['Penetration Testing', 'Exploitation', 'Social Engineering', 'Cryptography'],
    color: 'green',
  },
  {
    code: 'CISSP',
    name: 'Certified Information Systems Security Professional',
    nameKey: 'agency.training_course_cissp_title',
    level: 'Advanced',
    icon: '/images/training/cissp.png',
    desc: 'The gold standard in cybersecurity certification. Covers 8 CISSP domains from security & risk management to software development security.',
    descKey: 'agency.training_course_cissp_desc',
    topics: ['8 CISSP Domains', 'Risk Management', 'Access Control', 'Cryptography'],
    color: 'orange',
  },
  {
    code: 'SEC+',
    name: 'CompTIA Security+',
    nameKey: 'agency.training_course_sec_plus_title',
    level: 'Beginner',
    icon: '/images/training/comptia-security-plus.png',
    desc: 'The most widely adopted, vendor-neutral security certification. Perfect entry point for IT professionals moving into cybersecurity.',
    descKey: 'agency.training_course_sec_plus_desc',
    topics: ['Network Security', 'Compliance', 'Threats & Attacks', 'Cryptography'],
    color: 'teal',
  },
];

const levelColor = { Beginner: 'green', Intermediate: 'blue', Advanced: 'red' };

const levelKey = {
  Beginner: 'agency.training_level_beginner_label',
  Intermediate: 'agency.training_level_intermediate_label',
  Advanced: 'agency.training_level_advanced_label',
};

const topicKey = {
  'OWASP Top 10': 'agency.training_topic_owasp_top_10_item',
  'Burp Suite': 'agency.training_topic_burp_suite_item',
  'API Security': 'agency.training_topic_api_security_item',
  'Authentication Attacks': 'agency.training_topic_authentication_attacks_item',
  'IAM': 'agency.training_topic_iam_item',
  'KMS': 'agency.training_topic_kms_item',
  'CloudTrail': 'agency.training_topic_cloudtrail_item',
  'Security Hub': 'agency.training_topic_security_hub_item',
  'Network Defense': 'agency.training_topic_network_defense_item',
  'IDS/IPS': 'agency.training_topic_ids_ips_item',
  'Firewall Config': 'agency.training_topic_firewall_config_item',
  'Incident Response': 'agency.training_topic_incident_response_item',
  'SOC Operations': 'agency.training_topic_soc_operations_item',
  'Security Monitoring': 'agency.training_topic_security_monitoring_item',
  'SIEM': 'agency.training_topic_siem_item',
  'Threat Analysis': 'agency.training_topic_threat_analysis_item',
  'Cloud Architecture': 'agency.training_topic_cloud_architecture_item',
  'Data Security': 'agency.training_topic_data_security_item',
  'Compliance': 'agency.training_topic_compliance_item',
  'Risk Management': 'agency.training_topic_risk_management_item',
  'Incident Handling': 'agency.training_topic_incident_handling_item',
  'Threat Intel': 'agency.training_topic_threat_intel_item',
  'Digital Forensics': 'agency.training_topic_digital_forensics_item',
  'Malware Analysis': 'agency.training_topic_malware_analysis_item',
  'Penetration Testing': 'agency.training_topic_penetration_testing_item',
  'Exploitation': 'agency.training_topic_exploitation_item',
  'Social Engineering': 'agency.training_topic_social_engineering_item',
  'Cryptography': 'agency.training_topic_cryptography_item',
  '8 CISSP Domains': 'agency.training_topic_8_cissp_domains_item',
  'Access Control': 'agency.training_topic_access_control_item',
  'Network Security': 'agency.training_topic_network_security_item',
  'Threats & Attacks': 'agency.training_topic_threats_attacks_item',
};

const values = [
  { icon: '/images/training/passionate-educators.png', title: 'Passionate Educators', titleKey: 'agency.training_value_educators_title', color: 'green', desc: 'Industry experts committed to sharing deep knowledge and real-world experience from active cybersecurity careers.', descKey: 'agency.training_value_educators_desc' },
  { icon: '/images/training/student-centric.png', title: 'Student-Centric Approach', titleKey: 'agency.training_value_student_centric_title', color: 'orange', desc: 'Personalized learning paths that accommodate different skill levels, from beginners to experienced professionals.', descKey: 'agency.training_value_student_centric_desc' },
  { icon: '/images/training/industry-curriculum.png', title: 'Industry-Relevant Curriculum', titleKey: 'agency.training_value_curriculum_title', color: 'teal', desc: 'Programs developed collaboratively with industry leaders, emphasizing real-world application and current threat landscapes.', descKey: 'agency.training_value_curriculum_desc' },
];

export default function Training() {
  const { t } = useTranslation();
  return (
    <div className="training-page">
      {/* Hero */}
      <section className="page-hero">
        <div className="container page-hero-content">
          <span className="section-label section-label--blue">{t('agency.training_hero_badge', 'Education & Certification')}</span>
          <h1>{t('agency.training_hero_title', 'Cybersecurity')} <span style={{ color: '#d5772d' }}>{t('agency.training_hero_highlight_title', 'Training Programs')}</span></h1>
          <p>{t('agency.training_hero_desc', 'Bridge the knowledge gap with hands-on, industry-relevant training. Our expert-led programs prepare professionals at every level - from foundational skills to elite certifications.')}</p>
        </div>
      </section>

      {/* Value Props */}
      <section className="training-values">
        <div className="container">
          <div className="values-grid">
            {values.map((v, i) => (
              <div className="value-card" key={i}>
                <div className={`value-icon value-icon--${v.color}`}><img src={v.icon} alt={t(v.titleKey, v.title)} /></div>
                <h3>{t(v.titleKey, v.title)}</h3>
                <p>{t(v.descKey, v.desc)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Courses */}
      <section className="courses-section">
        <div className="container">
          <div className="section-header centered">
            <span className="section-label">{t('agency.training_courses_badge', 'Certification Programs')}</span>
            <h2 className="section-title">{t('agency.training_courses_title', '9 Industry-Recognized')} <span>{t('agency.training_courses_highlight_title', 'Programs')}</span></h2>
            <p className="section-desc" style={{ margin: '0 auto' }}>
              {t('agency.training_courses_desc', 'From entry-level foundations to advanced specialist certifications - find the right program for your career goals.')}
            </p>
          </div>
          <div className="courses-grid">
            {courses.map((course, i) => (
              <CourseCard key={i} course={course} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="training-cta">
        <div className="container">
          <div className="cta-inner">
            <div className="cta-glow" />
            <div className="cta-content">
              <h2>{t('agency.training_cta_title', 'Ready to Advance Your Cybersecurity Career?')}</h2>
              <p>{t('agency.training_cta_desc', 'Contact us today to explore how we can tailor our training programs to meet your specific needs and career objectives.')}</p>
              <div className="cta-actions">
                <Link to="/jobs" className="btn-outline-white">{t('agency.training_view_open_roles_btn', 'View Open Roles')}</Link>
                <Link to="/contact" className="btn-primary">
                  {t('agency.training_get_started_btn', 'Get Started Today')}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function CourseCard({ course }) {
  const { t } = useTranslation();
  return (
    <div className={`course-card course-card--${course.color}`}>
      <div className="course-header">
        <div className="course-icon"><img src={course.icon} alt={t(course.nameKey, course.name)} /></div>
        <div className="course-meta">
          <span className="course-code">{course.code}</span>
          <span className={`course-level level--${levelColor[course.level]}`}>{t(levelKey[course.level], course.level)}</span>
        </div>
      </div>
      <h3 className="course-name">{t(course.nameKey, course.name)}</h3>
      <p className="course-desc">{t(course.descKey, course.desc)}</p>
      <div className="course-topics">
        {course.topics.map(topic => (
          <span key={topic} className="topic-tag">{t(topicKey[topic], topic)}</span>
        ))}
      </div>
      <Link to="/contact" className="course-enroll">
        {t('agency.training_enroll_now_btn', 'Enroll Now')}
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
      </Link>
    </div>
  );
}
